// src/services/GeoRiskService.js
import auditService from './AuditService';
import moduleFreshnessService from './ModuleFreshnessService';
import storage from './StorageAdapter';
import entityService from './EntityService';
import ServiceValidator from './ServiceValidator';
import { testIndexes } from '../modules/GeoRisk/data/testIndexes';
import { initialRisks } from '../modules/BWRA/data/riskData';
import { fatfIndexSeedData, euHighRiskIndexSeedData } from '../modules/GeoRisk/data/fatfEuData';
import concurrencyService from './ConcurrencyService';
import georiskApi from '../api/georisk';
import { makeDualWriteMixin, applyDualWriteMixin } from './_dualWriteHelpers';

// Staleness thresholds per update frequency (in days).
// Exported so that modals and alarm components can share the same values.
export const STALENESS_THRESHOLDS = {
  DAILY:       2,
  WEEKLY:      10,
  MONTHLY:     45,
  QUARTERLY:   100,
  SEMI_ANNUAL: 200,
  YEARLY:      400,
};

// Predefined seed data for boolean index IDs
const PREDEFINED_INDEX_DATA = {
  '1': fatfIndexSeedData,   // FATF-status
  '3': euHighRiskIndexSeedData, // EU-högrisklistan
};

class GeoRiskService {
  constructor() {
    this.INDEXES_KEY = 'georisk_indexes';
    this.COUNTRIES_KEY = 'georisk_countries';
    this.COUNTRY_DATA_PREFIX = 'georisk_index_data_';
    this.MODEL_CONFIG_KEY = 'georisk_model_config';
    this.MODEL_VERSIONS_KEY = 'georisk_model_versions';
    this.INDEX_HISTORY_PREFIX = 'georisk_index_history_';
    this.VALIDATION_LAST_RUN_KEY = 'georisk_validation_last_run';
    this.RISK_HISTORY_KEY = 'georisk_risk_history';
    this.OVERRIDES_KEY = 'georisk_risk_overrides';
    this.OVERRIDE_CONFIG_KEY = 'georisk_override_config';
    this.listeners = [];
    // Migrate from global keys to entity-scoped keys
    this._migrateToEntityScope();
    // Re-notify listeners when active entity changes
    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _migrateToEntityScope() {
    const migrated = storage.getItem('georisk_entity_scope_migrated');
    if (migrated === 'true') return;

    const entityId = entityService.getActiveEntityId();
    if (!entityId) return;

    const jsonKeys = [this.INDEXES_KEY, this.COUNTRIES_KEY, this.MODEL_CONFIG_KEY, this.MODEL_VERSIONS_KEY];

    for (const key of jsonKeys) {
      const globalValue = storage.getJSON(key, null);
      const scopedKey = `${key}_${entityId}`;
      const scopedValue = storage.getJSON(scopedKey, null);
      if (globalValue !== null && scopedValue === null) {
        storage.setJSON(scopedKey, globalValue);
      }
    }

    // Also migrate country data keys (georisk_index_data_<indexId>)
    const indexes = storage.getJSON(this.INDEXES_KEY, null);
    if (indexes && Array.isArray(indexes)) {
      for (const index of indexes) {
        const dataKey = this.COUNTRY_DATA_PREFIX + index.id;
        const globalData = storage.getJSON(dataKey, null);
        const scopedDataKey = `${dataKey}_${entityId}`;
        const scopedData = storage.getJSON(scopedDataKey, null);
        if (globalData !== null && scopedData === null) {
          storage.setJSON(scopedDataKey, globalData);
        }
      }
    }

    storage.setItem('georisk_entity_scope_migrated', 'true');
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  // === INDEXES ===

  getIndexes() {
    const indexes = storage.getJSON(this._entityKey(this.INDEXES_KEY), null);
    if (indexes === null) {
      const entityId = entityService.getActiveEntityId();
      // Seed demo data only for the default entity or when no entity system exists (fresh install)
      // New entities (subsidiaries, branches, etc.) start with empty data
      if (!entityId || entityId === 'ent-001') {
        this._dualWriteJsonSync(this.INDEXES_KEY, testIndexes);
        return testIndexes;
      }
      this._dualWriteJsonSync(this.INDEXES_KEY, []);
      return [];
    }
    return indexes;
  }

  async saveIndexes(indexes) {
    await this._dualWriteJson(this.INDEXES_KEY, indexes);
    await moduleFreshnessService.markModified('GeoRisk');
    this.notifyListeners();
  }

  async addIndex(indexData) {
    ServiceValidator.validate(indexData, {
      name: { required: true, type: 'string', minLength: 1 },
    }, 'GeoRiskService.addIndex');
    const indexes = this.getIndexes();
    const newIndex = {
      ...indexData,
      id: String(Date.now()),
      lastUpdated: new Date().toISOString().split('T')[0],
      updatedBy: 'Current User',
      status: 'ACTIVE'
    };
    indexes.push(newIndex);
    await this.saveIndexes(indexes);
    auditService.log('CREATE', 'GeoRisk', {
      entity: 'Index',
      name: newIndex.name
    });
    return newIndex;
  }

  async updateIndex(indexId, updatedIndex) {
    ServiceValidator.requireString(indexId, 'indexId', 'GeoRiskService.updateIndex');
    ServiceValidator.validate(updatedIndex, {
      name: { required: true, type: 'string', minLength: 1 },
    }, 'GeoRiskService.updateIndex');
    const indexes = this.getIndexes();
    const idx = indexes.findIndex(i => i.id === indexId);
    if (idx >= 0) {
      indexes[idx] = { ...updatedIndex, id: indexId };
      await this.saveIndexes(indexes);
      auditService.log('UPDATE', 'GeoRisk', {
        entity: 'Index',
        name: updatedIndex.name
      });
      await concurrencyService.recordSave('georisk_index', indexId);
    }
    return indexes;
  }

  async deleteIndex(indexId) {
    const indexes = this.getIndexes();
    const deleted = indexes.find(i => i.id === indexId);
    const filtered = indexes.filter(i => i.id !== indexId);
    await this.saveIndexes(filtered);
    if (deleted) {
      auditService.log('DELETE', 'GeoRisk', {
        entity: 'Index',
        name: deleted.name
      });
    }
    return filtered;
  }

  // === MODEL CONFIGURATION ===

  getModelConfiguration(defaults = null) {
    return storage.getJSON(this._entityKey(this.MODEL_CONFIG_KEY), defaults);
  }

  async saveModelConfiguration(config) {
    // Preserve approval history when saving new config; reset status to 'draft'
    const existing = this.getModelConfiguration(null) || {};
    const savedConfig = {
      ...config,
      approvalStatus: 'draft',
      approvalHistory: existing.approvalHistory || [],
      approvedBy: null,
      approvedAt: null,
    };
    await this._dualWriteJson(this.MODEL_CONFIG_KEY, savedConfig);
    await moduleFreshnessService.markModified('GeoRisk');
    this.notifyListeners();
    auditService.log('UPDATE', 'GeoRisk', {
      entity: 'ModelConfiguration',
      modelType: config.modelType
    });
  }

  getModelVersions() {
    return storage.getJSON(this._entityKey(this.MODEL_VERSIONS_KEY), []);
  }

  async addModelVersion(version) {
    const versions = this.getModelVersions();
    versions.push(version);
    // Keep last 50 versions
    if (versions.length > 50) versions.splice(0, versions.length - 50);
    await this._dualWriteJson(this.MODEL_VERSIONS_KEY, versions);
    return version;
  }

  // === MODEL APPROVAL WORKFLOW (COMP-038) ===

  getModelApprovalStatus() {
    const config = this.getModelConfiguration(null);
    if (!config) return 'draft';
    return config.approvalStatus || 'draft';
  }

  async submitModelForReview() {
    const config = this.getModelConfiguration(null) || {};
    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = { from: config.approvalStatus || 'draft', to: 'submitted', by, at: now, comment: '' };
    const updated = {
      ...config,
      approvalStatus: 'submitted',
      approvalHistory: [...(config.approvalHistory || []), historyEntry]
    };
    await this._dualWriteJson(this.MODEL_CONFIG_KEY, updated);
    this.notifyListeners();
    auditService.log('STATUS_TRANSITION', 'GeoRisk', { entity: 'ModelConfiguration', from: historyEntry.from, to: 'submitted', by });
    return updated;
  }

  async approveModelConfiguration(comment = '') {
    const config = this.getModelConfiguration(null) || {};
    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = { from: config.approvalStatus || 'submitted', to: 'approved', by, at: now, comment };
    const updated = {
      ...config,
      approvalStatus: 'approved',
      approvedBy: by,
      approvedAt: now,
      approvalHistory: [...(config.approvalHistory || []), historyEntry]
    };
    await this._dualWriteJson(this.MODEL_CONFIG_KEY, updated);
    this.notifyListeners();
    auditService.log('STATUS_TRANSITION', 'GeoRisk', { entity: 'ModelConfiguration', from: historyEntry.from, to: 'approved', by });
    return updated;
  }

  async rejectModelConfiguration(comment = '') {
    const config = this.getModelConfiguration(null) || {};
    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = { from: config.approvalStatus || 'submitted', to: 'draft', by, at: now, comment };
    const updated = {
      ...config,
      approvalStatus: 'draft',
      approvedBy: null,
      approvedAt: null,
      approvalHistory: [...(config.approvalHistory || []), historyEntry]
    };
    await this._dualWriteJson(this.MODEL_CONFIG_KEY, updated);
    this.notifyListeners();
    auditService.log('STATUS_TRANSITION', 'GeoRisk', { entity: 'ModelConfiguration', from: historyEntry.from, to: 'draft', by });
    return updated;
  }

  _getCurrentUser() {
    try {
      const session = storage.getJSON('rms_session', null);
      return session?.username || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  // === INDEX VERSION HISTORY ===

  getIndexVersionHistory(indexId) {
    const key = this._entityKey(this.INDEX_HISTORY_PREFIX + indexId);
    return storage.getJSON(key, []);
  }

  async addIndexVersion(indexId, versionEntry) {
    const history = this.getIndexVersionHistory(indexId);
    history.unshift(versionEntry); // newest first
    if (history.length > 100) history.splice(100);
    const key = this._entityKey(this.INDEX_HISTORY_PREFIX + indexId);
    await storage.setJSON(key, history);
    return versionEntry;
  }

  // === COUNTRIES (CountryManager) ===

  getCountries() {
    return storage.getJSON(this._entityKey(this.COUNTRIES_KEY), null);
  }

  async saveCountries(countries) {
    await this._dualWriteJson(this.COUNTRIES_KEY, countries);
    this.notifyListeners();
  }

  async addCountry(country) {
    const { countries: initialCountries } = require('../modules/GeoRisk/data/countries');
    const current = this.getCountries() || initialCountries;
    const updated = [...current, country];
    await this.saveCountries(updated);
    auditService.log('CREATE', 'GeoRisk', {
      entity: 'Country',
      name: country.nameEn
    });
    return updated;
  }

  async updateCountry(alpha2, updatedData) {
    const { countries: initialCountries } = require('../modules/GeoRisk/data/countries');
    const current = this.getCountries() || initialCountries;
    const updated = current.map(c => c.alpha2 === alpha2 ? { ...c, ...updatedData } : c);
    await this.saveCountries(updated);
    auditService.log('UPDATE', 'GeoRisk', {
      entity: 'Country',
      alpha2,
      name: updatedData.nameEn
    });
    await concurrencyService.recordSave('georisk_country', alpha2);
    return updated;
  }

  async removeCountry(alpha2) {
    const { countries: initialCountries } = require('../modules/GeoRisk/data/countries');
    const current = this.getCountries() || initialCountries;
    const updated = current.filter(c => c.alpha2 !== alpha2);
    await this.saveCountries(updated);
    auditService.log('DELETE', 'GeoRisk', {
      entity: 'Country',
      alpha2
    });
    return updated;
  }

  // === COUNTRY DATA PER INDEX ===

  getCountryData(indexId) {
    const key = this._entityKey(this.COUNTRY_DATA_PREFIX + indexId);
    const stored = storage.getJSON(key, null);
    if (stored === null) {
      // Seed predefined index data on first access
      const seedData = PREDEFINED_INDEX_DATA[indexId] || [];
      storage.setJSON(key, seedData);
      return seedData;
    }
    return stored;
  }

  async saveCountryData(indexId, countryData) {
    const key = this._entityKey(this.COUNTRY_DATA_PREFIX + indexId);
    await storage.setJSON(key, countryData);
    await moduleFreshnessService.markModified('GeoRisk');
    this.notifyListeners();
  }

  // === BWRA RISK COVERAGE ===

  // Returns { categoryId: [{ id, name }] } — which BWRA categories each index declares it covers
  // Derives category from declaredRisks (new) or bwraCategories (legacy)
  getAllIndexCoverage() {
    const indexes = this.getIndexes();
    const coverage = {};
    indexes.forEach(idx => {
      const addEntry = (catId) => {
        if (!coverage[catId]) coverage[catId] = [];
        if (!coverage[catId].find(x => x.id === idx.id)) {
          coverage[catId].push({ id: idx.id, name: idx.name, status: idx.status });
        }
      };
      // New: derive category from declaredRisks
      const declaredRisks = idx.config?.declaredRisks || [];
      declaredRisks.forEach(riskId => {
        const risk = initialRisks.find(r => r.id === riskId);
        if (risk?.category) addEntry(risk.category);
      });
      // Legacy: direct bwraCategories
      const cats = idx.config?.bwraCategories || [];
      cats.forEach(catId => addEntry(catId));
    });
    return coverage;
  }

  // Returns { riskId: [{ id, name, status }] } — which specific BWRA risks each index declares it addresses
  getDeclaredRiskCoverage() {
    const indexes = this.getIndexes();
    const coverage = {};
    indexes.forEach(idx => {
      const risks = idx.config?.declaredRisks || [];
      risks.forEach(riskId => {
        if (!coverage[riskId]) coverage[riskId] = [];
        coverage[riskId].push({ id: idx.id, name: idx.name, status: idx.status });
      });
    });
    return coverage;
  }

  // === VALIDATION TIMESTAMP ===

  getValidationLastRun() {
    const stored = storage.getItem(this._entityKey(this.VALIDATION_LAST_RUN_KEY));
    return stored ? new Date(stored) : null;
  }

  async setValidationLastRun(date) {
    await this._dualWriteJson(this.VALIDATION_LAST_RUN_KEY, date.toISOString());
  }

  // === STALENESS ALARMS ===

  /**
   * Returns an array of alarm objects for indexes that have exceeded their
   * update-frequency staleness threshold.
   * Each alarm: { indexId, name, lastUpdated, daysStale, threshold, severity, affectedCountries }
   *   severity: 'warning' (threshold exceeded) | 'critical' (2x threshold exceeded)
   */
  getStalenessAlarms() {
    const indexes = this.getIndexes();
    const alarms = [];
    const now = Date.now();
    indexes.forEach(index => {
      if (index.status !== 'ACTIVE') return;
      const threshold = STALENESS_THRESHOLDS[index.updateFrequency];
      if (!threshold || !index.lastUpdated) return;
      let ageMs;
      try {
        ageMs = now - new Date(index.lastUpdated).getTime();
      } catch {
        return;
      }
      const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
      if (ageDays > threshold) {
        const countryData = this.getCountryData(index.id);
        alarms.push({
          indexId: index.id,
          name: index.name,
          lastUpdated: index.lastUpdated,
          daysStale: ageDays,
          threshold,
          severity: ageDays > threshold * 2 ? 'critical' : 'warning',
          affectedCountries: countryData.length,
        });
      }
    });
    // Most stale first
    alarms.sort((a, b) => b.daysStale - a.daysStale);
    return alarms;
  }

  // === COUNTRY RISK HISTORY (snapshots for time-series chart) ===

  getRiskHistory() {
    return storage.getJSON(this._entityKey(this.RISK_HISTORY_KEY), []);
  }

  /**
   * Save a snapshot of computed risk levels for all countries.
   * riskLevels: { [alpha2]: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | 'UNACCEPTABLE' | 'NOT_CALCULATED' }
   * If a snapshot already exists for today, it is overwritten with updated data.
   * Keeps max 52 snapshots (roughly one year of weekly data).
   */
  async saveRiskSnapshot(riskLevels) {
    const history = this.getRiskHistory();
    const now = new Date();
    const date = now.toISOString().split('T')[0];
    const entry = {
      timestamp: now.toISOString(),
      date,
      riskLevels,
    };
    const todayIdx = history.findIndex(h => h.date === date);
    if (todayIdx >= 0) {
      history[todayIdx] = entry;
    } else {
      history.push(entry);
      if (history.length > 52) history.splice(0, history.length - 52);
    }
    await this._dualWriteJson(this.RISK_HISTORY_KEY, history);
  }

  // === OVERRIDE CONFIGURATION ===

  /** Get review period config. Default: 90 days. */
  getOverrideConfig() {
    return storage.getJSON(this._entityKey(this.OVERRIDE_CONFIG_KEY), { reviewPeriodDays: 90 });
  }

  async saveOverrideConfig(config) {
    await this._dualWriteJson(this.OVERRIDE_CONFIG_KEY, config);
    this.notifyListeners();
  }

  // === COUNTRY RISK OVERRIDES ===

  /**
   * Returns a map of manual risk overrides keyed by alpha2 code.
   * Each entry: { level, reason, date, setBy, reviewDate, reviewHistory }
   */
  getOverrides() {
    return storage.getJSON(this._entityKey(this.OVERRIDES_KEY), {});
  }

  /**
   * Returns list of overrides older than the configured review period.
   * Each item includes daysSince (days since last review or initial set date).
   */
  getStaleOverrides() {
    const config = this.getOverrideConfig();
    const reviewPeriodDays = config.reviewPeriodDays || 90;
    const overrides = this.getOverrides();
    const today = new Date();
    const result = [];
    for (const [alpha2, entry] of Object.entries(overrides)) {
      const lastCheckDate = entry.reviewDate || entry.date;
      const setDate = new Date(lastCheckDate);
      const daysSince = Math.floor((today - setDate) / (1000 * 60 * 60 * 24));
      if (daysSince >= reviewPeriodDays) {
        result.push({ alpha2, ...entry, daysSince });
      }
    }
    return result;
  }

  /**
   * Save (or update) a manual risk override for one country.
   * @param {string} alpha2 - ISO alpha-2 country code
   * @param {string} level  - Risk level enum e.g. 'HIGH'
   * @param {string} reason - Mandatory justification text
   * @param {string} [setBy] - User name, defaults to 'Current User'
   */
  async saveOverride(alpha2, level, reason, setBy = 'Current User') {
    ServiceValidator.requireString(alpha2, 'alpha2', 'GeoRiskService.saveOverride');
    ServiceValidator.requireString(level, 'level', 'GeoRiskService.saveOverride');
    ServiceValidator.requireString(reason, 'reason', 'GeoRiskService.saveOverride');
    const overrides = this.getOverrides();
    const existing = overrides[alpha2];
    overrides[alpha2] = {
      level,
      reason,
      date: new Date().toISOString().split('T')[0],
      setBy,
      reviewDate: null,
      reviewHistory: existing?.reviewHistory || [],
    };
    await this._dualWriteJson(this.OVERRIDES_KEY, overrides);
    auditService.log('UPDATE', 'GeoRisk', {
      entity: 'RiskOverride',
      alpha2,
      level,
      reason,
    });
    this.notifyListeners();
  }

  /**
   * Renew (confirm) a manual override — resets the review clock.
   */
  async renewOverride(alpha2, reviewedBy = 'Current User') {
    const overrides = this.getOverrides();
    if (!overrides[alpha2]) return;
    const today = new Date().toISOString().split('T')[0];
    const reviewEntry = { date: today, reviewedBy };
    overrides[alpha2] = {
      ...overrides[alpha2],
      reviewDate: today,
      reviewHistory: [...(overrides[alpha2].reviewHistory || []), reviewEntry],
    };
    await this._dualWriteJson(this.OVERRIDES_KEY, overrides);
    auditService.log('UPDATE', 'GeoRisk', {
      entity: 'RiskOverride',
      action: 'REVIEW',
      alpha2,
      reviewedBy,
    });
    this.notifyListeners();
  }

  /**
   * Remove the manual risk override for a country.
   */
  async removeOverride(alpha2) {
    const overrides = this.getOverrides();
    if (!overrides[alpha2]) return;
    delete overrides[alpha2];
    await this._dualWriteJson(this.OVERRIDES_KEY, overrides);
    auditService.log('DELETE', 'GeoRisk', {
      entity: 'RiskOverride',
      alpha2,
    });
    this.notifyListeners();
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  // INFRA-181: backend dual-write + initFromBackend.
  async initFromBackend() {
    return this._initFromBackend(
      [this.INDEXES_KEY, this.COUNTRIES_KEY, this.MODEL_CONFIG_KEY,
       this.MODEL_VERSIONS_KEY, this.VALIDATION_LAST_RUN_KEY,
       this.RISK_HISTORY_KEY, this.OVERRIDES_KEY, this.OVERRIDE_CONFIG_KEY],
      [],
    );
  }
}

applyDualWriteMixin(GeoRiskService, makeDualWriteMixin({
  storage, api: georiskApi, serviceName: 'GeoRiskService',
}));

const geoRiskService = new GeoRiskService();

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.geoRiskService = geoRiskService;
}

export default geoRiskService;
